import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';

/**
 * Privacy Policy page
 */
export function PrivacyPage() {
  return (
    <div className="h-full flex flex-col overflow-hidden text-text-primary">
      {/* Header - fixed */}
      <header className="flex-shrink-0 bg-background/90 backdrop-blur-sm border-b border-surface">
        <div className="flex items-center gap-4 px-4 py-4">
          <Link
            to="/"
            className="p-2 hover:bg-surface rounded-lg transition-colors focus-visible-ring"
            aria-label="Back to home"
          >
            <ArrowLeft className="w-6 h-6" />
          </Link>
          <h1 className="text-xl font-bold">Privacy Policy</h1>
        </div>
      </header>

      {/* Content - scrollable */}
      <main className="flex-1 overflow-auto px-4 py-6 safe-bottom">
        <div className="prose prose-invert prose-sm">
          <p className="text-text-secondary text-sm mb-6">
            Last updated: {new Date().toLocaleDateString()}
          </p>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">1. Information We Collect</h2>
            <p className="text-text-secondary leading-relaxed mb-3">
              GPS Hunt collects the following types of information:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>
                <strong className="text-white">Account Information:</strong> Email address and display 
                name when you create an account
              </li>
              <li>
                <strong className="text-white">Location Data:</strong> Your device&apos;s GPS coordinates 
                while actively playing hunts
              </li>
              <li>
                <strong className="text-white">Photos:</strong> Images you upload as hints for pins (optional)
              </li>
              <li>
                <strong className="text-white">Device Information:</strong> Device type, operating system, 
                and app version for technical support
              </li>
              <li>
                <strong className="text-white">Usage Data:</strong> Game statistics such as hunts played, 
                items found, and completion times
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>To provide and maintain the GPS Hunt service</li>
              <li>To enable gameplay features including navigation and proximity detection</li>
              <li>To display your progress and statistics</li>
              <li>To improve and optimize the App</li>
              <li>To communicate with you about updates and support</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">3. Location Data</h2>
            <p className="text-text-secondary leading-relaxed mb-3">
              GPS Hunt requires location access to function. Your location is:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Only collected while you are actively participating in a hunt</li>
              <li>Shared only with other participants in the same hunt session</li>
              <li>Not stored permanently after a hunt session ends</li>
              <li>Never sold to third parties</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">4. Data Storage and Security</h2>
            <p className="text-text-secondary leading-relaxed">
              Your data is stored securely on Microsoft Azure servers. We implement industry-standard 
              security measures including encryption in transit and at rest. Hunt data is automatically 
              deleted after 24 hours of inactivity.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">5. Third-Party Services</h2>
            <p className="text-text-secondary leading-relaxed mb-3">
              GPS Hunt uses the following third-party services:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>
                <strong className="text-white">Firebase Authentication:</strong> For secure user 
                authentication
              </li>
              <li>
                <strong className="text-white">Azure Maps:</strong> For map display (subject to 
                Microsoft&apos;s privacy policy)
              </li>
              <li>
                <strong className="text-white">Azure Blob Storage:</strong> For photo storage
              </li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">6. Your Rights</h2>
            <p className="text-text-secondary leading-relaxed mb-3">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 text-text-secondary space-y-2">
              <li>Access your personal data</li>
              <li>Request deletion of your account and associated data</li>
              <li>Withdraw consent for location tracking at any time</li>
              <li>Export your game statistics</li>
            </ul>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">7. Children&apos;s Privacy</h2>
            <p className="text-text-secondary leading-relaxed">
              GPS Hunt is not intended for children under 13 years of age. We do not knowingly 
              collect personal information from children under 13. If you are a parent or guardian 
              and believe your child has provided us with personal information, please contact us.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">8. Changes to This Policy</h2>
            <p className="text-text-secondary leading-relaxed">
              We may update this Privacy Policy from time to time. We will notify you of any changes 
              by posting the new Privacy Policy on this page and updating the &quot;Last updated&quot; date.
            </p>
          </section>

          <section className="mb-8">
            <h2 className="text-lg font-semibold mb-3 text-white">9. Contact Us</h2>
            <p className="text-text-secondary leading-relaxed">
              If you have any questions about this Privacy Policy, please contact us through the app.
            </p>
          </section>
        </div>
      </main>
    </div>
  );
}

export default PrivacyPage;
